import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Shield, Clock, User, Database } from "lucide-react";
import { format } from "date-fns";

interface AuditLog {
  id: string;
  admin_user_id: string;
  action_type: string;
  target_user_id: string | null;
  target_resource_type: string | null;
  target_resource_id: string | null;
  action_details: any;
  created_at: string;
  admin_profile?: {
    full_name: string;
    email: string;
  };
}

export function AdminAuditLog() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      
      // Fetch audit logs
      const { data: logs, error: logsError } = await supabase
        .from('admin_action_audit')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (logsError) throw logsError;

      // Fetch admin profiles separately
      const adminIds = [...new Set(logs?.map(log => log.admin_user_id) || [])];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, email')
        .in('user_id', adminIds);

      if (profilesError) throw profilesError;

      // Merge the data
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      const enrichedLogs = logs?.map(log => ({
        ...log,
        admin_profile: profileMap.get(log.admin_user_id)
      })) || [];

      setAuditLogs(enrichedLogs);
    } catch (error: any) {
      toast({
        title: "Failed to load audit logs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getActionBadgeVariant = (actionType: string) => {
    if (actionType.includes('DELETE')) return 'destructive';
    if (actionType.includes('UPDATE') || actionType.includes('MODIFY')) return 'default';
    if (actionType.includes('VIEW') || actionType.includes('READ')) return 'secondary';
    return 'outline';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Admin Action Audit Log
        </CardTitle>
        <CardDescription>
          Comprehensive audit trail of all administrative actions for security monitoring
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No audit logs found
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target Resource</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">
                            {log.admin_profile?.full_name || 'Unknown'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {log.admin_profile?.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getActionBadgeVariant(log.action_type)}>
                        {log.action_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {log.target_resource_type && (
                        <div className="flex items-center gap-2">
                          <Database className="w-4 h-4 text-muted-foreground" />
                          <div className="text-sm">
                            <div className="font-medium capitalize">
                              {log.target_resource_type}
                            </div>
                            {log.target_resource_id && (
                              <div className="text-xs text-muted-foreground font-mono">
                                {log.target_resource_id.slice(0, 8)}...
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.action_details && (
                        <details className="cursor-pointer">
                          <summary className="text-xs text-muted-foreground hover:text-foreground">
                            View Details
                          </summary>
                          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-w-md">
                            {JSON.stringify(log.action_details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
